import type { AppErrorEvent } from '../services/appErrorMonitorService';
import type { TelemetryEventRecord } from '../services/telemetryMonitorService';

export type MonitoringIssueLevel = 'error' | 'warning';

export type MonitoringIssueInput = {
  kind: 'app_error' | 'telemetry';
  level: MonitoringIssueLevel;
  message: string;
  createdAt: string;
  source?: string | null;
  platform?: string | null;
  stack?: string | null;
  eventName?: string | null;
  url?: string | null;
  context?: Record<string, unknown> | null;
  tags?: Record<string, unknown> | null;
  payload?: Record<string, unknown> | null;
};

export type MonitoringIssueGroup = {
  dedupeKey: string;
  level: MonitoringIssueLevel;
  kinds: Array<'app_error' | 'telemetry'>;
  title: string;
  messageSample: string;
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  sources: string[];
  platforms: string[];
  screenOrPages: string[];
  actionNames: string[];
  edgeFunctions: string[];
  eventNames: string[];
  httpStatuses: string[];
  sampleStack?: string | null;
  sampleUrl?: string | null;
  sampleContext?: Record<string, unknown> | null;
  solutionId: string;
  solutionTitle: string;
  cursorPrompt: string;
};

type SolutionPattern = {
  id: string;
  title: string;
  match: (group: Omit<MonitoringIssueGroup, 'solutionId' | 'solutionTitle' | 'cursorPrompt'>) => boolean;
  buildPrompt: (group: Omit<MonitoringIssueGroup, 'solutionId' | 'solutionTitle' | 'cursorPrompt'>) => string;
};

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi;

export const normalizeIssueText = (value: string) =>
  value
    .toLowerCase()
    .replace(UUID_PATTERN, '<uuid>')
    .replace(/\b\d{4}-\d{2}-\d{2}t[\d:.-]+z?\b/g, '<timestamp>')
    .replace(/\b\d+(?:\.\d+)?\b/g, '<n>')
    .replace(/\s+/g, ' ')
    .trim();

const readContextString = (context: Record<string, unknown> | null | undefined, key: string) => {
  const value = context?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

const readContextNumber = (context: Record<string, unknown> | null | undefined, key: string) => {
  const value = context?.[key];
  return typeof value === 'number' ? String(value) : null;
};

const uniqueSorted = (values: Array<string | null | undefined>) =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value && value.trim())))).sort();

const truncate = (value: string, max: number) => (value.length > max ? `${value.slice(0, max)}...` : value);

const formatList = (values: string[], emptyLabel = 'unknown') => (values.length ? values.join(', ') : emptyLabel);

const formatTimestamp = (value: string) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const buildIssueTitle = (message: string, eventNames: string[]) => {
  const trimmed = message.trim();
  if (trimmed) return truncate(trimmed, 120);
  if (eventNames.length === 1) return eventNames[0];
  if (eventNames.length > 1) return `${eventNames[0]} (+${eventNames.length - 1} more)`;
  return 'Unknown monitoring issue';
};

export const monitoringIssueFromAppError = (event: AppErrorEvent): MonitoringIssueInput | null => {
  if (event.level !== 'error' && event.level !== 'warning') return null;
  return {
    kind: 'app_error',
    level: event.level,
    message: event.message,
    createdAt: event.created_at,
    source: event.source,
    platform: event.platform,
    stack: event.stack,
    url: event.url,
    context: event.context ?? null,
    tags: event.tags ?? null,
  };
};

export const monitoringIssueFromTelemetry = (event: TelemetryEventRecord): MonitoringIssueInput | null => {
  if (event.level !== 'error' && event.level !== 'warning') return null;
  return {
    kind: 'telemetry',
    level: event.level,
    message: event.message?.trim() || event.event_name,
    createdAt: event.created_at,
    source: event.source,
    platform: event.platform,
    eventName: event.event_name,
    url: event.url,
    context: event.context ?? null,
    tags: event.tags ?? null,
    payload: event.payload ?? null,
  };
};

export const buildMonitoringIssueDedupeKey = (issue: MonitoringIssueInput) => {
  const context = issue.context ?? {};
  const screenOrPage = readContextString(context, 'screenOrPage') ?? readContextString(context, 'pagePath') ?? '';
  const actionName = readContextString(context, 'actionName') ?? '';
  const edgeFunction = readContextString(context, 'edgeFunction') ?? '';
  const normalizedMessage = normalizeIssueText(issue.message || issue.eventName || issue.source || 'unknown');

  return [issue.level, normalizedMessage, screenOrPage, actionName, edgeFunction].join('|');
};

const buildGenericPrompt = (group: Omit<MonitoringIssueGroup, 'solutionId' | 'solutionTitle' | 'cursorPrompt'>) => {
  const lines = [
    `Investigate and fix this recurring ${group.level} in the NCOF monorepo (ChurchApp mobile + churchwebapp + Supabase).`,
    '',
    '## Issue summary',
    `- Title: ${group.title}`,
    `- Occurrences: ${group.count} (${formatTimestamp(group.firstSeenAt)} to ${formatTimestamp(group.lastSeenAt)})`,
    `- Sources: ${formatList(group.sources)}`,
    `- Platforms: ${formatList(group.platforms)}`,
    `- Screens/pages: ${formatList(group.screenOrPages)}`,
  ];

  if (group.actionNames.length) lines.push(`- Actions: ${formatList(group.actionNames)}`);
  if (group.edgeFunctions.length) lines.push(`- Edge functions: ${formatList(group.edgeFunctions)}`);
  if (group.eventNames.length) lines.push(`- Telemetry events: ${formatList(group.eventNames)}`);
  if (group.httpStatuses.length) lines.push(`- HTTP statuses: ${formatList(group.httpStatuses)}`);

  lines.push(
    '',
    '## Sample message',
    group.messageSample,
  );

  if (group.sampleUrl) {
    lines.push('', '## Sample URL', group.sampleUrl);
  }

  if (group.sampleStack) {
    lines.push('', '## Sample stack', '```', group.sampleStack, '```');
  }

  if (group.sampleContext && Object.keys(group.sampleContext).length > 0) {
    lines.push('', '## Sample context', '```json', JSON.stringify(group.sampleContext, null, 2), '```');
  }

  lines.push(
    '',
    '## What to do',
    '1. Search the codebase for the root cause of this recurring issue.',
    '2. Implement the smallest safe fix that stops the error/warning from recurring.',
    '3. Add or update tests if the failure mode is easy to regress.',
    '4. Summarize the root cause, files changed, and how to verify the fix.',
  );

  return lines.join('\n');
};

const SOLUTION_PATTERNS: SolutionPattern[] = [
  {
    id: 'realtime-disconnect',
    title: 'Realtime / WebSocket disconnect',
    match: (group) => {
      const haystack = [
        group.messageSample,
        ...group.eventNames,
        ...group.actionNames,
        ...group.screenOrPages,
      ]
        .join(' ')
        .toLowerCase();
      return /realtime|websocket|ws_connected|socketstate|channel subscribe|reconnect/.test(haystack);
    },
    buildPrompt: (group) =>
      [
        'Investigate recurring Supabase Realtime disconnects in ChurchApp.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Occurrences: ${group.count}`,
        `- Screens/pages: ${formatList(group.screenOrPages)}`,
        `- Last seen: ${formatTimestamp(group.lastSeenAt)}`,
        '',
        '## Likely areas',
        '- `ChurchApp/src/services/supabaseService.ts` and realtime subscription helpers',
        '- `ChurchApp/src/utils/telemetry.ts` / reconnect logic',
        '- Performance snapshots showing `ws_connected = false` in Super Admin Performance',
        '',
        '## Tasks',
        '1. Trace why channels drop or fail to resubscribe after background/foreground or network changes.',
        '2. Fix reconnect/backoff so subscriptions recover without spamming errors.',
        '3. Ensure disconnect root cause is logged once with actionable context, not noisy warnings.',
        '4. Verify on a device by backgrounding the app, toggling network, and confirming realtime reconnects.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'session-loss',
    title: 'Auth session loss / refresh failure',
    match: (group) => {
      const haystack = [group.messageSample, ...group.eventNames, ...group.actionNames].join(' ').toLowerCase();
      return /session-loss|auth-session-refresh|refresh session|invalid refresh token|jwt expired|signed out/.test(
        haystack,
      );
    },
    buildPrompt: (group) =>
      [
        'Investigate enforced session loss / auth refresh failures in ChurchApp.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Occurrences: ${group.count}`,
        `- Screens/pages: ${formatList(group.screenOrPages)}`,
        `- Telemetry/events: ${formatList(group.eventNames, 'n/a')}`,
        '',
        '## Likely areas',
        '- `ChurchApp/src/auth/sharedRefresh.ts` force/token refresh + rotation-race recovery',
        '- `ChurchApp/src/components/AppStateManager.tsx` foreground coordinated refresh',
        '- `ChurchApp/src/context/AuthContext.tsx` SIGNED_OUT / TOKEN_REFRESH_FAILED soft recovery',
        '- `ChurchApp/src/services/supabaseClient.ts` SecureStore persist (`AFTER_FIRST_UNLOCK`) + locked-keychain skip',
        '- Telemetry event `auth-session-refresh` with `failureClass` + stable `reason` (`rotation-race:*`, `invalid-session:*`, `transient:secure-store-locked`)',
        '',
        '## Tasks',
        '1. Identify which refresh label/path failed (`payload.label`, `failureClass`, `refreshPath`) and whether logout was enforced (it should not be for rotation races or `transient:secure-store-locked`).',
        '2. Distinguish true invalid sessions (`invalid_session`) from rotation races (`rotation_race`) and transient/network/timeout/locked-store failures.',
        '3. Improve recovery UX and logging so Super Admin can see the failing path/reason clearly.',
        '4. Verify login persists across app resume and token refresh cycles.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'upload-queue-pressure',
    title: 'Upload queue backlog / pressure',
    match: (group) => /upload queue|upload_queue|queued upload|pending upload/.test(group.messageSample.toLowerCase()),
    buildPrompt: (group) =>
      [
        'Investigate upload queue pressure/backlog in ChurchApp.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Occurrences: ${group.count}`,
        `- Platforms: ${formatList(group.platforms)}`,
        '',
        '## Likely areas',
        '- Upload queue / background upload worker in ChurchApp',
        '- Performance snapshots with high `upload_queue_depth`',
        '- Failed retries that requeue indefinitely',
        '',
        '## Tasks',
        '1. Find why uploads accumulate instead of draining.',
        '2. Add backoff, dedupe, and failure handling for stuck queue items.',
        '3. Ensure queue depth telemetry reflects real user impact, not test noise.',
        '4. Verify uploads drain after reconnecting network and reopening the app.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'edge-function-failure',
    title: 'Supabase Edge Function failure',
    match: (group) =>
      group.edgeFunctions.length > 0 ||
      /edge function|functions\/v1\/|invoke edge|non-2xx response/i.test(group.messageSample),
    buildPrompt: (group) =>
      [
        'Investigate a recurring Supabase Edge Function failure.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Edge functions: ${formatList(group.edgeFunctions)}`,
        `- HTTP statuses: ${formatList(group.httpStatuses, 'unknown')}`,
        `- Actions: ${formatList(group.actionNames, 'unknown')}`,
        `- Occurrences: ${group.count}`,
        '',
        '## Tasks',
        '1. Locate the client caller and the edge function implementation in `supabase/functions/` or `churchwebapp/supabase/functions/`.',
        '2. Inspect auth headers, request payload validation, and non-2xx handling.',
        '3. Fix the underlying failure and preserve backward compatibility for existing callers.',
        '4. Add logging with requestId/httpStatus and verify the happy path plus the previously failing case.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'http-auth-failure',
    title: 'HTTP auth / permission failure',
    match: (group) => {
      const statuses = group.httpStatuses.join(',');
      const haystack = group.messageSample.toLowerCase();
      return (
        /401|403|unauthorized|forbidden|permission denied|row-level security|rls/.test(`${statuses} ${haystack}`) ||
        group.httpStatuses.some((status) => status === '401' || status === '403')
      );
    },
    buildPrompt: (group) =>
      [
        'Investigate recurring auth/permission failures (401/403/RLS) in the NCOF app.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- HTTP statuses: ${formatList(group.httpStatuses, 'unknown')}`,
        `- Screens/pages: ${formatList(group.screenOrPages)}`,
        `- Actions: ${formatList(group.actionNames, 'unknown')}`,
        `- Occurrences: ${group.count}`,
        '',
        '## Tasks',
        '1. Trace the failing query/RPC and whether the user/session lacks required role or RLS policy coverage.',
        '2. Check recent migrations in `supabase/migrations/` for policy regressions.',
        '3. Fix client-side assumptions or server-side policies with the smallest safe change.',
        '4. Verify with the affected screen/page and role combination.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'network-timeout',
    title: 'Network timeout / slow request',
    match: (group) => /timed out|timeout|etimedout|network request failed|fetch failed|aborted/.test(group.messageSample.toLowerCase()),
    buildPrompt: (group) =>
      [
        'Investigate recurring network timeouts or slow failing requests.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Screens/pages: ${formatList(group.screenOrPages)}`,
        `- Actions: ${formatList(group.actionNames, 'unknown')}`,
        `- Occurrences: ${group.count}`,
        '',
        '## Tasks',
        '1. Find the request/query that times out and whether retry/backoff is missing or too aggressive.',
        '2. Reduce payload size, add pagination, or move heavy work off the critical path if needed.',
        '3. Improve recoverable warning handling so transient failures do not look like hard errors.',
        '4. Verify on slow/unstable network conditions.',
        '',
        '## Sample message',
        group.messageSample,
      ].join('\n'),
  },
  {
    id: 'unhandled-error',
    title: 'Unhandled runtime error',
    match: (group) => {
      const sources = group.sources.join(' ').toLowerCase();
      return /errorutils|unhandledrejection|window\.onerror|global_error_handler/.test(sources);
    },
    buildPrompt: (group) =>
      [
        'Investigate an unhandled runtime error in the app.',
        '',
        '## Observed issue',
        `- ${group.title}`,
        `- Sources: ${formatList(group.sources)}`,
        `- Screens/pages: ${formatList(group.screenOrPages)}`,
        `- Occurrences: ${group.count}`,
        '',
        '## Tasks',
        '1. Use the sample stack trace to locate the crashing code path.',
        '2. Add defensive handling for null/undefined/async failure cases.',
        '3. Ensure the error is recoverable where possible and logged with useful context.',
        '4. Verify the screen/action no longer triggers an unhandled exception.',
        '',
        '## Sample stack',
        group.sampleStack ? ['```', group.sampleStack, '```'].join('\n') : 'No stack captured.',
      ].join('\n'),
  },
];

const resolveSolution = (group: Omit<MonitoringIssueGroup, 'solutionId' | 'solutionTitle' | 'cursorPrompt'>) => {
  const matched = SOLUTION_PATTERNS.find((pattern) => pattern.match(group));
  if (matched) {
    return {
      solutionId: matched.id,
      solutionTitle: matched.title,
      cursorPrompt: matched.buildPrompt(group),
    };
  }
  return {
    solutionId: 'generic',
    solutionTitle: 'General fix investigation',
    cursorPrompt: buildGenericPrompt(group),
  };
};

export const aggregateMonitoringIssues = (issues: MonitoringIssueInput[]): MonitoringIssueGroup[] => {
  const groups = new Map<string, Omit<MonitoringIssueGroup, 'solutionId' | 'solutionTitle' | 'cursorPrompt'>>();

  issues.forEach((issue) => {
    const dedupeKey = buildMonitoringIssueDedupeKey(issue);
    const context = issue.context ?? {};
    const screenOrPage = readContextString(context, 'screenOrPage') ?? readContextString(context, 'pagePath');
    const actionName = readContextString(context, 'actionName');
    const edgeFunction = readContextString(context, 'edgeFunction');
    const httpStatus = readContextNumber(context, 'httpStatus');
    const existing = groups.get(dedupeKey);

    if (!existing) {
      groups.set(dedupeKey, {
        dedupeKey,
        level: issue.level,
        kinds: [issue.kind],
        title: buildIssueTitle(issue.message, issue.eventName ? [issue.eventName] : []),
        messageSample: issue.message.trim() || issue.eventName || 'Unknown issue',
        count: 1,
        firstSeenAt: issue.createdAt,
        lastSeenAt: issue.createdAt,
        sources: uniqueSorted([issue.source]),
        platforms: uniqueSorted([issue.platform]),
        screenOrPages: uniqueSorted([screenOrPage]),
        actionNames: uniqueSorted([actionName]),
        edgeFunctions: uniqueSorted([edgeFunction]),
        eventNames: uniqueSorted([issue.eventName]),
        httpStatuses: uniqueSorted([httpStatus]),
        sampleStack: issue.stack ?? null,
        sampleUrl: issue.url ?? null,
        sampleContext: issue.context ?? null,
      });
      return;
    }

    existing.count += 1;
    if (new Date(issue.createdAt).getTime() < new Date(existing.firstSeenAt).getTime()) {
      existing.firstSeenAt = issue.createdAt;
    }
    if (new Date(issue.createdAt).getTime() > new Date(existing.lastSeenAt).getTime()) {
      existing.lastSeenAt = issue.createdAt;
      existing.messageSample = issue.message.trim() || existing.messageSample;
      if (issue.stack) existing.sampleStack = issue.stack;
      if (issue.url) existing.sampleUrl = issue.url;
      if (issue.context) existing.sampleContext = issue.context;
    }
    if (!existing.kinds.includes(issue.kind)) existing.kinds.push(issue.kind);
    existing.sources = uniqueSorted([...existing.sources, issue.source]);
    existing.platforms = uniqueSorted([...existing.platforms, issue.platform]);
    existing.screenOrPages = uniqueSorted([...existing.screenOrPages, screenOrPage]);
    existing.actionNames = uniqueSorted([...existing.actionNames, actionName]);
    existing.edgeFunctions = uniqueSorted([...existing.edgeFunctions, edgeFunction]);
    existing.eventNames = uniqueSorted([...existing.eventNames, issue.eventName]);
    existing.httpStatuses = uniqueSorted([...existing.httpStatuses, httpStatus]);
    existing.title = buildIssueTitle(existing.messageSample, existing.eventNames);
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      ...resolveSolution(group),
    }))
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime();
    });
};
